import React, { useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Autocomplete, Box, Checkbox, Chip, Paper, TextField, Tooltip, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import { getUsageCategoryList, Usage, UsageCategoryKey, UsageInterval, UsageIntervalKey, UsageMetric } from "routes/apiUsage/types/Usage";
import { CategoricalChartState } from "recharts/types/chart/types";
import UsageIntervalTooltip from "./UsageIntervalTooltip";
import { toAbbreviatedNumber } from "components/formatting/AbbreviateNumber";

const UsageCategoryList = getUsageCategoryList();

interface RollingUsageBarChartProps {
  metric: UsageMetric;
  onChange: (selectedUsageCategories: Array<UsageCategoryKey>) => void;
  selectedUsageCategories: Array<UsageCategoryKey>;
  usage: Usage;
  usageCategoryOptions: Array<UsageCategoryKey>;
}

export default function RollingUsageBarChart(props: RollingUsageBarChartProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const [hovered, setHovered] = useState<UsageInterval | undefined>();

  const timeline = (props?.usage?.timeline || []).reduce((result, current, index, arr): Array<UsageInterval> => {
    const copy: UsageInterval = {...current};
    if (index > 0) {
      const previous = result[index-1];
      const allKeys = Object.assign({}, current, previous);
      const keys = Object.keys(allKeys) as Array<UsageIntervalKey>;
      for (const key of keys) {
        if (key !== "date") {
          copy[key] = (current[key] || 0) + (previous[key] || 0);
        }
      }
    }
    result.push(copy);
    return result;
  }, []);

  const bars = (props.selectedUsageCategories || []).map((usageCategoryKey, index) => {
    const colorIndex = UsageCategoryList.indexOf(usageCategoryKey);
    return <Bar
      key={usageCategoryKey}
      dataKey={`${usageCategoryKey}_${props.metric}`}
      fill={theme.colors.namedColors.chartColorArray[colorIndex]}
      stackId={props.metric}
    />
  });

  return (
    <Paper sx={{p: 1}}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end"
        }}>
        <Autocomplete
          disableClearable
          disableCloseOnSelect
          multiple
          options={props.usageCategoryOptions}
          onChange={(event, value) => props.onChange(value) }
          renderInput={(params) => <TextField autoComplete="off" label="Usage Categories" {...params} />}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                <Checkbox
                  checked={selected}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  size="small"
                  style={{ marginRight: 8 }}
                />
                {i18n.t(`usage.categories.${option}`)}
              </li>
            );
          }}
          renderValue={(value: readonly string[], getItemProps) =>
            value.map((option: string, index: number) => {
              const { key, ...tagProps } = getItemProps({ index });
              const colorIndex = UsageCategoryList.indexOf(option as UsageCategoryKey);
              return (
                <Chip
                  key={option}
                  label={i18n.t(`usage.categories.${option}`)}
                  sx={{backgroundColor: theme.colors.namedColors.chartColorArray[colorIndex]}}
                  {...tagProps}
                />
              );
            })
          }
          size="small"
          sx={{minWidth: 200, width: "fit-content"}}
          value={props.selectedUsageCategories}
        />
      </Box>
      <Tooltip
        followCursor
        open={!!hovered}
        title={hovered &&
          <UsageIntervalTooltip
            activeUsageCategories={props.selectedUsageCategories}
            activeUsageMetric={props.metric}
            usageInterval={hovered}
          />}
      >
        <Box>
          <ResponsiveContainer height={240} width="100%">
            <BarChart
              data={timeline}
              onMouseLeave={() => setHovered(undefined)}
              onMouseMove={(nextState: CategoricalChartState, ...args) => {
                let usageInterval: UsageInterval | undefined = undefined;
                if (typeof nextState.activeTooltipIndex === "number" && nextState.activeTooltipIndex >= 0) {
                  usageInterval = timeline[nextState.activeTooltipIndex];
                }
                setHovered(usageInterval);
              }}
            >
              <CartesianGrid strokeOpacity={0.65} strokeDasharray="3 3" />
              <XAxis dataKey="date" interval="preserveStartEnd" />
              <YAxis tickFormatter={(value) => {
                let abbreviated = toAbbreviatedNumber(i18n, value);
                return abbreviated.toString();
              }} />
              {bars}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Tooltip>
    </Paper>
  );
}